import React from 'react'
import i18n from 'live/services/i18n'
import DateService from 'live/services/date'
import TimeRange from 'live/components/ui/time-range'
import DashboardLink from 'live/components/ui/DashboardLink'
import DefaultAnnotationActions from '../../annotations/annotation-default-actions'
import { MarkersAnnotation } from '../api'
import MarkerCurves from './annotations/CurvesList'
import MarkerAnnotationFormConfigs from './annotations/MarkerFormConfigs'

interface MarkerEventRendererProps {
    event: MarkersAnnotation
    onClick: (event: MarkersAnnotation) => void
    onEdit: (event: MarkersAnnotation, config: Record<string, unknown>) => void
    expanded: boolean
    mode: string
    dashboardId: number
    dateTimeFormatter?: (value: number | Date) => string
}

const MarkerEventRenderer = (variant: 'timeline' | 'messenger'): React.FC<MarkerEventRendererProps> => (
    props: MarkerEventRendererProps
) => {
    const now = new Date()
    const nowTime = now.getTime()

    const { onClick, event, expanded, mode, dashboardId, dateTimeFormatter } = props
    const { message, dashboard, author, createdAt = now, __color, begin = nowTime, end } = event
    const mCreatedAt = DateService.getMoment(createdAt)
    const dashboardSpan = event.dashboardSpan && `${event.dashboardSpan} shifted to ts ${event.createdAt}`
    const spanType = event.extra.target.spanType

    return (
        <div
            onClick={expanded ? null : () => onClick(event)}
            className={`live-annotation ${expanded ? 'live-annotation--expanded' : ''}`}
        >
            <div className="live-annotation__title">{i18n('Marker')}</div>
            {expanded ? (
                <DefaultAnnotationActions
                    {...props}
                    onEdit={event => props.onEdit(event, MarkerAnnotationFormConfigs)}
                />
            ) : null}

            <div className="live-annotation__subtitle">
                {i18n('by')}{' '}
                <span className="live-annotation__author">
                    <small>{author || i18n('Missing author')}</small>
                </span>
                {', '}
                <span className="live-annotation__note-time" title={mCreatedAt.format(DateService.getDateTimeFormat())}>
                    {typeof dateTimeFormatter === 'function'
                        ? dateTimeFormatter(createdAt)
                        : DateService.fromNowCalendar(createdAt)}
                </span>
                {spanType === 'time' && dashboard && dashboardId ? (
                    <span>
                        {` ${i18n('on')} `}
                        <DashboardLink
                            name={event.dashboard}
                            id={event.dashboardId}
                            filters={event.dashboardFilters}
                            span={dashboardSpan}
                            begin={event.begin}
                            end={event.end}
                        />
                    </span>
                ) : null}
            </div>
            <div className="live-annotation__time-range">
                <TimeRange color={__color} begin={begin} end={end} mode={spanType === 'time' ? 'temporal' : spanType} />
            </div>

            {expanded && (
                <React.Fragment>
                    <hr style={{ marginTop: '10px', marginBottom: '10px' }} />

                    <MarkerCurves widgets={event.extra.widgets} />
                </React.Fragment>
            )}

            {expanded ? (
                <div className="live-annotation__expanded-content">
                    <div className="live-annotation__message mt-15">
                        <div className="live-annotation__title mb-10">{i18n('Note text')}</div>
                        <p style={{ fontSize: '12px', color: '#606A80' }}>{message || i18n('No note text')}</p>
                    </div>
                </div>
            ) : (
                <div className="live-annotation__message-preview">{getMessagePreview(event.message)}</div>
            )}
        </div>
    )
}

function getMessagePreview(message: string, length = 90): string {
    if (!message || typeof message !== 'string') return ''

    return `${message.substr(0, length)}${message.length > length ? '...' : ''}`
}

export default function EventRenderer(variant: 'timeline' | 'messenger') {
    return function (
        event: Record<string, unknown>
    ): { priority: number; component: React.FC<MarkerEventRendererProps> } {
        if (isMarkerEvent(event)) {
            return {
                priority: 99,
                component: MarkerEventRenderer(variant)
            }
        }
    }
}

function isMarkerEvent(event: Record<string, unknown>): boolean {
    return event.__type === '__annotations' && event.__src === 'markers'
}
